import { EDecision, EFinancingProgram, EProvince } from '@if/core/types'
import { FormContainer } from '@if/mui'
import CalculateIcon from '@mui/icons-material/Calculate'
import { Stack, Typography } from '@mui/material'
import { PageError, PageSpinner } from '@src/components'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { BaseWorksheetFormConfig } from '@src/components/WorksheetForm'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { Merchant } from '@src/data/types/Merchant'
import { useTranslation } from 'react-i18next'
import { CalculatorValues } from './calculatorSchema'
import CalculatorFields from './components/CalculatorFields'
import { useCalculatorCompute } from './hooks/useCalculatorCompute'
import { useCalculatorFormFields, useCalculatorFormSetup } from './hooks/useCalculatorFormSetup'

const CalculatorPage = () => {
  const { creditApplication, worksheet, isLoading, isError } = useCreditApplicationData()

  if (isLoading) {
    return <PageSpinner isLoading />
  }

  if (isError || !creditApplication) {
    return <PageError errors={[{ message: 'calculator.errors.loadFailed' }]} />
  }

  return <CalculatorPageReady creditApplication={creditApplication} worksheet={worksheet ?? null} />
}

interface CalculatorPageReadyProps {
  creditApplication: CreditApplication
  worksheet: BaseWorksheetDto | null
}

// Inner component runs only once the credit application is loaded — keeps the
// setup hook (and downstream useForm) seeded with real values from first render.
const CalculatorPageReady = ({ creditApplication, worksheet }: CalculatorPageReadyProps) => {
  const finalDecision = creditApplication.finalCreditDecision
  const prequalDecision = creditApplication.prequalificationDecision
  const hasFinalDecision = finalDecision != null && finalDecision.decision !== EDecision.Pending

  const decisionInterestRate = hasFinalDecision
    ? finalDecision.interestRate
    : (prequalDecision?.minInterestRate ?? null)
  const maxAmountFinanced = hasFinalDecision
    ? finalDecision.maxAmountFinanced
    : (prequalDecision?.maxLoanAmount ?? null)
  const maxTermDuration = hasFinalDecision ? finalDecision.maxTermDuration : (prequalDecision?.maxLoanTerm ?? null)
  // Prequal has no affordability cap; leave maxPmtAmount undefined so the form
  // skips payment-cap validation and term filtering instead of using a sentinel.
  const maxPmtAmount = hasFinalDecision ? finalDecision.maxPmtAmount : undefined
  const isEstimate = !hasFinalDecision && prequalDecision != null

  const setup = useCalculatorFormSetup({
    creditApplicationId: creditApplication.id,
    financingProgramId: creditApplication.financingProgramId,
    merchantId: creditApplication.merchantId ?? null,
    expiresOn: creditApplication.expiresOn,
    createdOn: creditApplication.createdOn,
    decisionInterestRate,
    maxAmountFinanced,
    maxTermDuration,
    maxPmtAmount,
    hasCoApplicant: !!creditApplication.coApplicant,
    stateIso: creditApplication.applicant.currentAddress.stateIso,
    normsCreditDecision: creditApplication.normsCreditDecision,
    isSinglePayment: creditApplication.isSinglePayment,
    creditRslaPlanName: creditApplication.applicant.creditDecision?.planName,
  })

  if (!setup.config) {
    return <PageSpinner isLoading />
  }

  return (
    <CalculatorPageForm
      config={setup.config}
      availablePromoCodes={setup.availablePromoCodes}
      merchant={setup.merchant}
      interestRate={setup.interestRate}
      worksheet={worksheet}
      requestedLoanAmount={creditApplication.requestedLoanAmount ?? null}
      financingProgramId={creditApplication.financingProgramId}
      stateIso={creditApplication.applicant.currentAddress.stateIso}
      hasCoApplicant={!!creditApplication.coApplicant}
      creditApplicationId={creditApplication.id}
      expiresOn={creditApplication.expiresOn}
      isEstimate={isEstimate}
    />
  )
}

interface CalculatorPageFormProps {
  config: BaseWorksheetFormConfig
  availablePromoCodes: Set<string>
  merchant: Merchant | null
  interestRate: number
  worksheet: BaseWorksheetDto | null
  requestedLoanAmount: number | null
  financingProgramId: EFinancingProgram
  stateIso: EProvince
  hasCoApplicant: boolean
  creditApplicationId: string
  expiresOn: Date | string
  isEstimate: boolean
}

const CalculatorPageForm = ({
  config,
  availablePromoCodes,
  merchant,
  interestRate,
  worksheet,
  requestedLoanAmount,
  financingProgramId,
  stateIso,
  hasCoApplicant,
  creditApplicationId,
  expiresOn,
  isEstimate,
}: CalculatorPageFormProps) => {
  const { t } = useTranslation()

  const useFormSetup = () => {
    const { schema, defaultValues } = useCalculatorFormFields({
      config,
      availablePromoCodes,
      worksheet,
      requestedLoanAmount,
    })
    return { schema, defaultValues, mode: 'onBlur' as const }
  }

  const { compute, result, isComputing } = useCalculatorCompute({
    config,
    financingProgramId,
    hasCoApplicant,
    stateIso,
    creditApplicationId,
    merchantId: merchant?.id,
    interestRate,
    insuranceCompanyId: merchant?.insuranceProviderIds?.[0] ?? null,
  })

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={1} component={Typography} variant="h3">
        <CalculateIcon color="primary" fontSize="inherit" />
        <span>{t('calculator.title')}</span>
      </Stack>
      <FormContainer<CalculatorValues>
        useFormSetup={useFormSetup}
        role="edit"
        onSubmit={(values) => compute(values)}
        submitLabel={t('calculator.calculate')}
      >
        {({ control }) => (
          <CalculatorFields
            control={control}
            config={config}
            result={result}
            isComputing={isComputing}
            interestRate={interestRate}
            expiresOn={expiresOn}
            isEstimate={isEstimate}
          />
        )}
      </FormContainer>
    </Stack>
  )
}

export default CalculatorPage
